<script setup lang="ts">
import { waitFor } from "xstate";
import { useSelector } from "@xstate/vue";
import { useRoute, useRouter } from "vue-router";
import { computed, onMounted, ref, watch } from "vue";
import { ToastProvider, ToastRoot, ToastViewport, ToastAction, ToastDescription, ToastTitle } from "reka-ui";

import Loading from "@/components/Loading.vue";
import AuthError from "@/pages/Auth.vue";
import UserDetails from "@/components/Layout/UserDetails.vue";

import { useIsPopout } from "@/hooks/use-is-popout";
import { useRootLayout } from "@/hooks/use-root-layout";
import { useNetworkStatus } from "@/hooks/use-network-status";

import { useBrandStore, useMessageStore, useUserSessionStore } from "@/stores";

import { authMachine } from "@/machines/auth-machine";

import type { RouteTitles } from "@/router";

const isError = useSelector(authMachine, (state) => state.matches("error"));

const route = useRoute();
const router = useRouter();
const isPopout = useIsPopout();
const rootLayout = useRootLayout();
const isOnline = useNetworkStatus();

const isReady = ref(false);
const isNavigating = ref(false);
const authCompletedOnce = ref(false);

const pageTitle = computed(() => {
  if (route.meta.showHeading === false) return;

  if (route.meta.isCaseReference) {
    const store = useBrandStore();
    if (store.viewerConfig.menuBar?.hideCaseTitle === true) {
      return;
    }
  }

  const title = route.meta.pageTitle as RouteTitles;
  if (typeof title === "function") return title(route.params, false);

  return title;
});

router.beforeEach(() => (isNavigating.value = true));
router.afterEach(() => (isNavigating.value = false));

authMachine.on("UNAUTHENTICATED", () => void router.push("/auth"));

watch([isReady, isError], () => {
  if (authCompletedOnce.value || !isReady.value || isError.value) return;
  authCompletedOnce.value = true;
});

onMounted(async () => {
  try {
    isReady.value = false;
    await waitFor(authMachine, (snapshot) => snapshot.hasTag("AuthComplete"));

    const brandStore = useBrandStore();
    const userSessionStore = useUserSessionStore();

    await Promise.all([brandStore.loadMapping(), userSessionStore.loadGlobalSession()]);

    if (brandStore.selectedBrand != null) return;

    let brandToSelect: string | undefined;
    const lastUsedBrand = userSessionStore.globalSession.lastUsedBrand?.toLowerCase();

    if (lastUsedBrand == null) {
      const availableBrands = Object.keys(brandStore.mappings).sort((a, b) => a.localeCompare(b));

      if (availableBrands.find((key) => key.toLowerCase() === lastUsedBrand) != null) return;
      else brandToSelect = availableBrands[0];
    } else brandToSelect = lastUsedBrand;

    await router.push({ name: "brand", params: { brand: brandToSelect } });
  } catch (e) {
    console.error(e);
    useMessageStore().updateMessage({
      title: "Initialization",
      message: "An error occured while loading the page.",
      type: "error",
      showReload: true,
      autoHide: false,
    });
  } finally {
    isReady.value = true;
  }
});

function focusMainContent() {
  rootLayout.mainContentRef.value?.focus();
}
</script>
<template>
  <template v-if="isPopout" />
  <div
    v-else-if="authCompletedOnce"
    class="tw-grid tw-h-full tw-grid-cols-[max-content_minmax(0,1fr)] tw-grid-rows-[max-content_minmax(0,1fr)]"
  >
    <ToastProvider :duration="Infinity">
      <ToastRoot
        type="background"
        class="tw-overflow-hidden tw-border tw-border-orange-500 tw-bg-white"
        :open="isError && authCompletedOnce"
      >
        <div
          :style="{ gridTemplateAreas: `'title action' 'description action'` }"
          class="tw-grid tw-grid-cols-[minmax(0,1fr)_max-content] tw-bg-orange-200/30 tw-p-4 tw-shadow"
        >
          <ToastTitle class="tw-font-semibold tw-tracking-wide" :style="{ gridArea: 'title' }">
            Authentication Error
          </ToastTitle>
          <ToastDescription :style="{ gridArea: 'description' }" class="tw-text-xs">
            Error refreshing account session. Use the button to manually retry the process and prevent data loss.
          </ToastDescription>
          <div :style="{ gridArea: 'action' }">
            <ToastAction
              alt-text="Reload the page"
              class="tw-btn-md-gray tw-rounded"
              @click="() => authMachine.send({ type: 'EXTERNAL_LOGIN' })"
            >
              Log In
            </ToastAction>
          </div>
        </div>
      </ToastRoot>
      <ToastRoot :open="!isOnline" type="background" class="tw-overflow-hidden tw-border tw-border-red-800 tw-bg-white">
        <div class="tw-grid tw-bg-red-300/20 tw-p-4 tw-shadow">
          <ToastTitle class="tw-font-semibold tw-tracking-wide">No Internet Connection</ToastTitle>
          <ToastDescription class="tw-text-xs">
            Your internet connection has been lost. Some features may be unavailable until the connection is restored.
          </ToastDescription>
        </div>
      </ToastRoot>
      <header
        aria-label="Header Navigation"
        class="tw-bg-green tw-relative tw-col-span-2 tw-row-span-1 tw-flex tw-w-full tw-flex-wrap tw-items-center tw-justify-start tw-gap-1 tw-border-gray-500 tw-px-2 tw-py-2.5 sm:tw-px-4"
      >
        <button
          type="button"
          class="tw-fixed tw-rounded-md tw-bg-black tw-p-2 tw-text-lg tw-text-white tw-underline [&:not(:focus)]:tw-sr-only"
          @click="focusMainContent"
        >
          Skip to main content
        </button>
        <component
          :is="route.params.brand ? 'router-link' : 'span'"
          title="Go to Case Search"
          class="tw-block tw-shrink-0 tw-pr-1 sm:tw-pr-4"
          :to="route.params.brand ? `/${route.params.brand}` : undefined"
        >
          <img src="/logo-header.png" alt="Recordsure Logo" class="tw-aspect-square tw-w-4 sm:tw-w-8" />
        </component>
        <h1
          v-if="pageTitle"
          aria-atomic="true"
          aria-live="assertive"
          :aria-busy="isNavigating"
          class="tw-m-0 tw-border-0 tw-p-0 tw-text-sm tw-font-semibold tw-tracking-wide sm:tw-text-xl"
        >
          {{ pageTitle }}
        </h1>
        <h1 v-else class="tw-sr-only">Case View</h1>
        <div
          :ref="rootLayout.headerRef"
          class="tw-flex tw-flex-1 tw-flex-wrap tw-items-center tw-justify-start tw-gap-[inherit]"
        />
        <div
          v-if="rootLayout.layout.value == 'FIXED'"
          class="tw-flex tw-flex-wrap tw-items-center tw-justify-start tw-gap-[inherit]"
        >
          <div :ref="rootLayout.sidebarRef" class="tw-flex tw-flex-col tw-gap-[inherit]"></div>
          <UserDetails />
        </div>
      </header>
      <aside
        v-if="rootLayout.layout.value == 'FLEX'"
        aria-label="Sidebar"
        class="tw-bg-green tw-col-start-1 tw-row-start-2 tw-flex tw-flex-col tw-gap-2 tw-overflow-y-auto tw-p-1 sm:tw-p-2"
      >
        <div :ref="rootLayout.sidebarRef" class="tw-flex tw-flex-col tw-gap-[inherit]"></div>
        <div aria-hidden="true" class="tw-flex-1"></div>
        <UserDetails />
      </aside>
      <main
        :ref="rootLayout.mainContentRef"
        tabindex="0"
        aria-label="Main Content"
        class="tw-col-start-2 tw-row-start-2"
      >
        <router-view />
        <form
          v-if="route.path === '/'"
          class="tw-m-auto tw-flex tw-h-full tw-max-w-sm tw-flex-col tw-items-center tw-justify-center tw-gap-2"
          @submit.prevent="
            (e) => {
              const brand = ((e.target as HTMLFormElement).querySelector('[name=brand]') as HTMLInputElement)?.value;
              if (!brand) return;

              router.push({ name: 'brand', params: { brand } });
            }
          "
        >
          <img src="/logo-header.png" alt="Recordsure Logo" class="tw-h-20 tw-w-20" />
          <h4 class="tw-text-3xl tw-font-bold">Incomplete URL!</h4>
          <p class="text-lg tw-text-balance tw-text-center">
            Type your brand in the box below and select the button to view the home page
          </p>
          <input
            required
            type="text"
            placeholder="Brand"
            name="brand"
            class="tw-rounded-lg tw-border tw-border-gray-500 tw-bg-white tw-p-2 tw-uppercase"
          />
          <button
            type="submit"
            aria-label="Go to Brand Home"
            class="tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-bg-gray-500 tw-px-4 tw-py-2"
          >
            <i class="fas fa-home"></i>
            Go to Home
          </button>
        </form>
      </main>
      <ToastViewport
        class="tw-fixed tw-bottom-3 tw-right-3 tw-z-[100000] tw-w-full tw-max-w-md tw-space-y-2 tw-rounded-md [&>*]:tw-overflow-hidden [&>*]:tw-rounded-[inherit]"
      />
    </ToastProvider>
  </div>
  <Loading v-else-if="!isError" id="loading" message="Authenticating..." />
  <AuthError v-else />
</template>
